import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'

import { cn } from '~/utils/classnames'

const IMAGES = Array.from(
  { length: 12 },
  (_, i) => `/assets/images/${i + 1}.webp`
)

const IMAGE_DURATION = 5000

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[j] as T
    shuffled[j] = temp as T
  }
  return shuffled
}

interface ImageBackgroundProps {
  className?: string
  aspectRatio?: 'portrait' | 'cover'
}

export const ImageBackground = ({
  className,
  aspectRatio = 'cover',
}: ImageBackgroundProps) => {
  const [shuffledImages, setShuffledImages] = useState(IMAGES)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isClient, setIsClient] = useState(false)

  // Shuffle only on client to avoid hydration mismatch
  useEffect(() => {
    setShuffledImages(shuffleArray(IMAGES))
    setIsClient(true)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % shuffledImages.length)
    }, IMAGE_DURATION)

    return () => clearInterval(interval)
  }, [shuffledImages.length])

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        aspectRatio === 'portrait' && 'aspect-[3/4]',
        aspectRatio === 'cover' && 'absolute inset-0',
        className
      )}
    >
      {isClient ? (
        <AnimatePresence mode="popLayout">
          <motion.img
            key={shuffledImages[currentIndex]}
            src={shuffledImages[currentIndex]}
            alt=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, ease: 'easeInOut' }}
            className={cn(
              'absolute inset-0 h-full w-full object-cover',
              aspectRatio === 'portrait' && 'rounded-lg'
            )}
          />
        </AnimatePresence>
      ) : (
        <img
          src={IMAGES[0]}
          alt=""
          className={cn(
            'absolute inset-0 h-full w-full object-cover',
            aspectRatio === 'portrait' && 'rounded-lg'
          )}
        />
      )}
    </div>
  )
}

export default ImageBackground
